import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Select, Textarea, Empty } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Truck, RefreshCw, Printer, Download, XCircle, FileText } from "lucide-react";

function carrierEventTime(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value * 1000;
  if (typeof value !== "string" || !value.trim()) return null;

  const text = value.trim();
  let normalized = text;
  const localIso = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?))?$/;
  const dayFirst = /^(\d{2})[/-](\d{2})[/-](\d{4})[ T](\d{2}:\d{2}:\d{2})$/;
  const isoMatch = text.match(localIso);
  const dayMatch = text.match(dayFirst);
  if (isoMatch) {
    normalized = `${isoMatch[1]}T${isoMatch[2] || "00:00:00"}+05:30`;
  } else if (dayMatch) {
    normalized = `${dayMatch[3]}-${dayMatch[2]}-${dayMatch[1]}T${dayMatch[4]}+05:30`;
  }

  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [parcelForm, setParcelForm] = useState({
    weightGrams: "", lengthCm: "", widthCm: "", heightCm: "", description: "",
  });
  const [reviewShippingEstimate, setReviewShippingEstimate] = useState(false);

  const load = useCallback(() => {
  return api
    .get(`/admin/orders/${id}`)
    .then(({ data }) => {
      setData(data);
      setNewStatus("");
      const parcel = data.order.parcelPacking || {};
      setParcelForm({
        weightGrams: parcel.weightGrams ?? "",
        lengthCm: parcel.lengthCm ?? "",
        widthCm: parcel.widthCm ?? "",
        heightCm: parcel.heightCm ?? "",
        description: parcel.description || "",
      });
      setReviewShippingEstimate(parcel.shippingEstimateReviewed === true);
    });
}, [id]);

useEffect(() => {
  load();
}, [load]);

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  const o = data.order;
  const sh = o.shipping || {};
  const pickup = sh.pickup || {};
  const paid = o.paymentStatus === "PAID";
  const closed = ["CANCELLED", "REFUND_INITIATED", "REFUNDED"].includes(o.status);
  const pickupExists = Object.keys(pickup).length > 0;
  const pickupCanRetry = ["NOT_PICKED", "CANCELLED", "FAILED"].includes(pickup.status) && !pickup.scheduling;
  // The carrier (Delhivery) cancellation failed or is unconfirmed. NOT_CANCELLABLE /
  // REJECTED means retrying cannot help: the parcel has to be handled manually.
  const cancelRequired = sh.cancelRequired === true;
  const cancelClassification = sh.cancelClassification || "";
  const cancelManual = ["NOT_CANCELLABLE", "REJECTED"].includes(cancelClassification);
  // Whether automated packing-slip retrieval is enabled server-side. The label routes
  // answer 404 when it is off, so the buttons must never fire in that state.
  const labelsEnabled = data.labelsEnabled === true;
  const labelsDisabledHint = "Automated labels are not enabled; use Delhivery One";
  // Newest-first carrier events as recorded by the tracking guard; `applied === false`
  // marks an event the staleness guard ignored (kept for the audit trail).
  const carrierHistory = [...(sh.history || [])]
    .map((entry, index) => ({ entry, index, instant: carrierEventTime(entry?.at) }))
    .filter(({ entry }) => entry && entry.status)
    .sort((a, b) => {
      if (a.instant === null) return b.instant === null ? a.index - b.index : 1;
      if (b.instant === null) return -1;
      return b.instant - a.instant || a.index - b.index;
    })
    .map(({ entry }) => entry);
  const rtoPhaseLabel = (sh.rtoPhase || "").replace(/^RTO_/, "").replace(/_/g, " ").toLowerCase();
  const activeAwb = sh.awb && sh.cancelStatus !== "CANCELLED"
    && String(sh.status || "").toLowerCase() !== "cancelled"
    && !["CANCELLED", "REFUND_INITIATED", "REFUNDED"].includes(o.status);
  const pickupId = pickup.requestId || pickup.pickupId || pickup.id;
  const pickupLabel = pickup.scheduling
    ? "REQUEST IN PROGRESS"
    : pickup.status
      ? `${pickup.status.replace(/_/g, " ")}${pickup.date ? ` · ${pickup.date}` : ""}`
      : pickupExists
        ? "NEEDS VERIFICATION"
        : sh.awb
          ? "NOT REQUESTED"
          : "—";
  const parcel = o.parcelPacking || {};
  const parcelValues = {
    weightGrams: parcelForm.weightGrams === "" ? null : Number(parcelForm.weightGrams),
    lengthCm: parcelForm.lengthCm === "" ? null : Number(parcelForm.lengthCm),
    widthCm: parcelForm.widthCm === "" ? null : Number(parcelForm.widthCm),
    heightCm: parcelForm.heightCm === "" ? null : Number(parcelForm.heightCm),
    description: parcelForm.description.trim() || null,
  };
  const checkoutWeight = Number(o.shippingWeightGrams);
  const materialShippingDifference = o.shippingRateSource === "courier"
    && o.shippingWeightEstimated === true
    && Number.isFinite(checkoutWeight) && checkoutWeight > 0
    && parcelValues.weightGrams !== null
    && Math.abs(parcelValues.weightGrams - checkoutWeight) / checkoutWeight >= 0.2;
  const parcelConfirmed = parcel.confirmed === true
    && ["weightGrams", "lengthCm", "widthCm", "heightCm"].every(
      (field) => parcelValues[field] === parcel[field],
    )
    && parcelValues.description === (parcel.description || null)
    && (!materialShippingDifference
      || (parcel.shippingEstimateReviewed === true && reviewShippingEstimate));
  // COD is fulfilable while its payment is still PENDING (cash is collected only after
  // delivery); prepaid orders are fulfilable only once the payment is PAID.
  const prepaidPaidForFulfilment = o.paymentMethod !== "COD" && paid;
  const codPendingForFulfilment = o.paymentMethod === "COD" && o.paymentStatus === "PENDING";
  const parcelEligible = (prepaidPaidForFulfilment || codPendingForFulfilment)
    && o.deliveryMethod === "COURIER" && !closed
    && !sh.awb && !sh.manifesting && !sh.manualReviewRequired;

  const saveParcelPacking = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/orders/${id}/parcel-packing`, {
          ...parcelValues,
          reviewedShippingEstimate: reviewShippingEstimate,
      });
      await load();
      toast.success("Parcel measurements saved and confirmed");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); await load(); toast.success(`${label} complete`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const createShipment = () => run("Shipment", () => api.post(`/admin/orders/${id}/shipment`));
  const createPickup = async () => {
    setBusy("Pickup");
    try {
      const { data: result } = await api.post(`/admin/orders/${id}/pickup`, {});
      await load();
      if (result.needsVerification) {
        toast.warning("Pickup request outcome is unknown. Verify it with Delhivery before retrying.");
      } else {
        toast.success("Pickup request sent");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const syncTracking = () => run("Tracking sync", () => api.post(`/admin/orders/${id}/sync`));
  const openInvoice = async () => {
    try {
      const { data: d } = await api.get(`/admin/orders/${id}/invoice`);
      const html = await api.get(`/admin/invoices/${d.invoice.id}/html`,
        { responseType: "text", transformResponse: (r) => r });
      const w = window.open("", "_blank");
      if (w) { w.document.write(html.data); w.document.close(); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancelOrder = async () => {
    const prompt = o.paymentMethod === "COD" && paid
      ? "Cancel this collected COD order? No Razorpay refund will be issued; arrange any cash reimbursement separately."
      : "Cancel this order and issue a refund?";
    if (!window.confirm(prompt)) return;
    setBusy("Cancel");
    try {
      const { data: result } = await api.post(`/admin/orders/${id}/cancel`, { reason: "Cancelled by admin", refund: true });
      await load();
      if (result.warnings?.length) toast.warning(result.warnings.join(" "));
      else toast.success("Cancel complete");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  // The packing slip is fetched through the authenticated API client as a blob — a
  // plain link would not carry the admin token. A failure returns a Blob body, so the
  // error detail is unwrapped before it reaches the toast.
  const labelErrorMessage = async (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.detail) {
          return typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
        }
      } catch { /* a non-JSON body falls through to the status message below */ }
    } else if (typeof data?.detail === "string") {
      return data.detail;
    }
    if (status === 404) {
      return "No packing slip found for this order (or the label route is disabled). "
        + "Use Delhivery One for this waybill.";
    }
    return apiError(e);
  };
  const labelBlobUrl = async () => {
    if (!labelsEnabled) {
      // Defensive: the buttons are disabled in this state, so this must never fire.
      toast.warning(labelsDisabledHint);
      return null;
    }
    const res = await api.get(`/admin/orders/${id}/label/pdf`, { responseType: "blob" });
    return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  };
  const downloadLabel = async () => {
    setBusy("Label");
    try {
      const url = await labelBlobUrl();
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = `packing-slip-${sh.awb || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(await labelErrorMessage(e)); }
    finally { setBusy(""); }
  };
  const printLabel = async () => {
    setBusy("Print label");
    try {
      const url = await labelBlobUrl();
      if (!url) return;
      const w = window.open(url, "_blank");
      if (w) {
        setTimeout(() => { try { w.focus(); w.print(); } catch { /* the PDF viewer may block printing */ } }, 1000);
      }
    } catch (e) { toast.error(await labelErrorMessage(e)); }
    finally { setBusy(""); }
  };
  const retryCarrierCancellation = async () => {
    setBusy("Retry carrier cancel");
    try {
      const { data: result } = await api.post(`/admin/orders/${id}/retry-carrier-cancellation`);
      await load();
      if (result.ok) {
        toast.success(result.duplicate
          ? "Delhivery had already cancelled this shipment"
          : "Carrier cancellation confirmed");
      } else {
        toast.warning(`${result.message}${result.retryable
          ? " You can retry once the courier is reachable."
          : " The carrier will not cancel it — handle the parcel manually (RTO)."}`);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const recordCODCollection = async () => {
    const due = Number(o.codAmountDue ?? o.total);
    const evidenceNote = window.prompt("Enter the cash collection evidence or receipt reference:");
    if (!evidenceNote?.trim()) return;
    if (!window.confirm(`Confirm that ₹${due.toFixed(2)} was physically collected for this delivered COD order?`)) return;
    setBusy("COD collection");
    try {
      const { data: result } = await api.post(`/admin/orders/${id}/cod-collection`, {
        amountReceived: due,
        evidenceNote: evidenceNote.trim(),
      });
      await load();
      if (result.warnings?.length) toast.warning(result.warnings.join(" "));
      else toast.success("COD collection recorded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const updateStatus = async () => {
    if (!newStatus) return;
    if (["CANCELLED", "REFUND_INITIATED", "REFUNDED"].includes(newStatus)
        && !window.confirm(`Move this order to ${newStatus.replace(/_/g, " ")}? This cannot be undone from the order page.`)) {
      return;
    }
    setSaving(true);
    try { await api.put(`/admin/orders/${id}/status`, { status: newStatus, note }); toast.success("Status updated"); setNote(""); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <button onClick={() => navigate("/admin/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-3"><ArrowLeft className="h-4 w-4" /> Back to orders</button>
      <AdminHeader title={o.orderNumber} subtitle={`Placed ${new Date(o.createdAt).toLocaleString("en-IN")}`} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Items</h3>
            <div className="space-y-3">
              {o.items.map((it) => (
                <div key={it.id} className="flex items-center gap-3">
                  <img src={resolveMediaUrl(it.image)} alt="" onError={handleMediaError} className="h-12 w-12 rounded bg-slate-50 object-contain" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1">{it.name}</p><p className="text-xs text-slate-500 font-mono">{it.sku} · Qty {it.quantity}{it.attributes && Object.keys(it.attributes).length > 0 ? " · " + Object.entries(it.attributes).map(([k, v]) => `${k}:${v}`).join(", ") : ""}</p></div>
                  <span className="font-mono text-sm">{formatINR(it.lineTotal)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 mt-4 pt-4 space-y-1.5 text-sm max-w-xs ml-auto">
              <Row label="Subtotal" value={formatINR(o.subtotal)} />
              {o.discount > 0 && <Row label={`Discount${o.couponCode ? ` (${o.couponCode})` : o.discountLabel ? ` — ${o.discountLabel}` : ""}${o.discountSource === "auto" ? " (automatic)" : ""}`} value={`− ${formatINR(o.discount)}`} />}
              <Row label="Shipping" value={o.shippingCharge === 0 ? "Free" : formatINR(o.shippingCharge)} />
              {!!o.handlingFee && <Row label="Handling" value={formatINR(o.handlingFee)} />}
              {!!o.tax && <Row label="Tax" value={formatINR(o.tax)} />}
              <div className="flex justify-between font-semibold pt-1 border-t border-slate-100"><span>Total</span><span className="font-mono">{formatINR(o.total)}</span></div>
              <Row label="Payment method" value={o.paymentMethod === "COD" ? "Cash on Delivery" : "Razorpay"} />
              <Row label="Payment status" value={o.paymentStatus || "PENDING"} />
              {o.paymentMethod === "COD" && o.paymentStatus !== "PAID" &&
                <Row label="Amount due" value={formatINR(o.amountDue ?? o.codAmountDue ?? o.total)} />}
              {o.paymentCollection?.collectedAt &&
                <Row label="Collected" value={new Date(o.paymentCollection.collectedAt).toLocaleString("en-IN")} />}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Order Timeline</h3>
            <div className="relative pl-6">
              {o.timeline?.map((t, i) => (
                <div key={i} className="relative pb-5 last:pb-0">
                  <span className="absolute -left-6 top-1 h-3 w-3 rounded-full bg-amber-500 ring-4 ring-amber-100" />
                  {i < o.timeline.length - 1 && <span className="absolute -left-[19px] top-4 bottom-0 w-0.5 bg-slate-200" />}
                  <p className="font-semibold text-sm">{t.status.replace(/_/g, " ")}</p>
                  {t.note && <p className="text-xs text-slate-500">{t.note}</p>}
                  <p className="text-xs text-slate-400" title={t.at}>
                    {new Date(t.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  </p>
                </div>
              ))}
            </div>
          </Card>
          <div data-testid="carrier-timeline">
            <Card className="p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Carrier timeline</h3>
              {carrierHistory.length === 0 ? (
                <Empty text="No carrier events yet" />
              ) : (
                <div className="relative pl-6">
                  {carrierHistory.map((event, i) => (
                    <div key={`${event.at || "event"}-${i}`} className="relative pb-5 last:pb-0">
                      <span className={`absolute -left-6 top-1 h-3 w-3 rounded-full ring-4 ${event.applied === false ? "bg-slate-300 ring-slate-100" : "bg-emerald-500 ring-emerald-100"}`} />
                      {i < carrierHistory.length - 1 && <span className="absolute -left-[19px] top-4 bottom-0 w-0.5 bg-slate-200" />}
                      <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                        {event.status}
                        {event.applied === false && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                            data-testid="carrier-event-ignored">ignored (older event)</span>
                        )}
                      </p>
                      {event.location && <p className="text-xs text-slate-500">{event.location}</p>}
                      <p className="text-xs text-slate-400" title={event.at}>
                        {event.at ? new Date(event.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>

        <div className="space-y-6">
          {parcelEligible && (
            <Card className="p-5" data-testid="parcel-packing-section">
              <h3 className="font-semibold text-slate-900 mb-1">Parcel Packing</h3>
              <p className="text-xs text-slate-500 mb-3">
                Enter measurements of the complete packed box or mailer. Product weights and dimensions in the item snapshots are not outer-parcel measurements.
              </p>
              <p className="text-xs text-slate-700 bg-slate-50 rounded-lg p-2 mb-3">
                Product weights describe the items only. Total packed weight below must include the items and packaging.
              </p>
              {o.shippingWeightEstimated === true && (
                <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2 mb-3" data-testid="shipping-estimate-note">
                  Checkout shipping was {formatINR(o.shippingCharge)} using an estimated package weight of {o.shippingWeightGrams} g. This is not an exact carrier quote; the customer-paid shipping amount remains unchanged at {formatINR(o.shippingCharge)}.
                </p>
              )}
              <form onSubmit={saveParcelPacking} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs font-medium text-slate-600">
                    Total packed weight (g)
                    <input aria-label="Total packed weight (g)" data-testid="parcel-weight"
                      type="number" min="0.01" max="50000" step="any" required
                      value={parcelForm.weightGrams}
                      onChange={(e) => {
                        setParcelForm({ ...parcelForm, weightGrams: e.target.value });
                        setReviewShippingEstimate(false);
                      }}
                      className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Outer length (cm)
                    <input aria-label="Outer length (cm)" data-testid="parcel-length"
                      type="number" min="0.01" max="200" step="any" required
                      value={parcelForm.lengthCm}
                      onChange={(e) => setParcelForm({ ...parcelForm, lengthCm: e.target.value })}
                      className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Outer width (cm)
                    <input aria-label="Outer width (cm)" data-testid="parcel-width"
                      type="number" min="0.01" max="200" step="any" required
                      value={parcelForm.widthCm}
                      onChange={(e) => setParcelForm({ ...parcelForm, widthCm: e.target.value })}
                      className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Outer height (cm)
                    <input aria-label="Outer height (cm)" data-testid="parcel-height"
                      type="number" min="0.01" max="200" step="any" required
                      value={parcelForm.heightCm}
                      onChange={(e) => setParcelForm({ ...parcelForm, heightCm: e.target.value })}
                      className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm" />
                  </label>
                </div>
                <Textarea label="Package note (optional)" rows={2} maxLength={250}
                  value={parcelForm.description}
                  onChange={(e) => setParcelForm({ ...parcelForm, description: e.target.value })} />
                {materialShippingDifference && (
                  <label className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 rounded-lg p-2">
                    <input type="checkbox" checked={reviewShippingEstimate}
                      onChange={(e) => setReviewShippingEstimate(e.target.checked)} />
                    <span>
                      Packed weight differs materially from the checkout estimate ({o.shippingWeightGrams} g). I reviewed this difference; do not change or charge the customer again for the paid shipping amount.
                    </span>
                  </label>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs ${parcelConfirmed ? "text-emerald-700" : "text-amber-700"}`}
                    data-testid="parcel-confirmation-status">
                    {parcelConfirmed ? "Saved and confirmed" : parcel.confirmed ? "Edits not saved — confirm again" : "Not yet saved or confirmed"}
                  </span>
                  <Btn type="submit" variant="outline" loading={saving}
                    disabled={materialShippingDifference && !reviewShippingEstimate}
                    data-testid="save-parcel-packing">
                    Save &amp; confirm
                  </Btn>
                </div>
              </form>
            </Card>
          )}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="h-4 w-4 text-slate-500" />
              <h3 className="font-semibold text-slate-900">Fulfillment</h3>
              {sh.fulfillmentStatus === "RTO" && (
                <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700"
                  data-testid="rto-badge">
                  RTO{rtoPhaseLabel ? ` · ${rtoPhaseLabel}` : ""}
                </span>
              )}
            </div>
            <dl className="text-sm space-y-1.5">
              <Row label="Payment" value={o.paymentStatus} />
              {o.paymentMethod === "COD" && o.paymentStatus !== "PAID" &&
                <Row label="Amount due" value={formatINR(o.amountDue ?? o.codAmountDue ?? o.total)} />}
              <Row label="Fulfillment" value={sh.fulfillmentStatus || o.fulfillmentStatus || "—"} />
              <Row label="AWB" value={sh.awb || "—"} />
              <Row label="Shipment status" value={sh.status || "—"} />
              <Row label="Pickup" value={pickupLabel} />
              {pickupId && <Row label="Pickup ID" value={pickupId} />}
              <Row label="Tracking updated" value={sh.lastTrackingUpdate ? new Date(sh.lastTrackingUpdate).toLocaleString("en-IN") : "—"} />
              {o.refund?.razorpayRefundId && <Row label="Refund" value={`${o.refund.status || "INITIATED"} · ₹${o.refund.amount ?? ""}`} />}
            </dl>
            {sh.manifestError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2 mt-3" data-testid="fulfillment-error">
                Shipping error: {sh.manifestError.message}
              </p>
            )}
            {sh.manifestReconciliation?.required && (
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3"
                data-testid="manifest-reconciliation">
                <p className="font-semibold">Carrier response needs manual reconciliation. Do not retry shipment creation.</p>
                <dl className="space-y-1 mt-2">
                  <Row label="Remote waybill" value={sh.manifestReconciliation.remoteShipment?.waybill || "Not returned"} />
                  <Row label="Carrier order reference" value={sh.manifestReconciliation.remoteShipment?.refnum || "Not returned"} />
                  <Row label="Carrier serviceable" value={sh.manifestReconciliation.remoteShipment?.serviceable === true
                    ? "Yes" : sh.manifestReconciliation.remoteShipment?.serviceable === false ? "No" : "Unknown"} />
                  <Row label="Carrier package status" value={sh.manifestReconciliation.remoteShipment?.status || "Unknown"} />
                </dl>
              </div>
            )}
            {cancelRequired && (
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3"
                data-testid="carrier-cancel-required">
                <p className="font-semibold">Delhivery has not confirmed the cancellation.</p>
                <p className="mt-1">
                  {cancelManual
                    ? "The carrier will not cancel this shipment any more (already picked up / in transit). Handle the parcel manually — for example let it come back as RTO — and close it out when it returns."
                    : "The outcome is unknown or temporary. Retry the carrier cancellation; if it keeps failing, contact Delhivery before writing the parcel off."}
                </p>
                {cancelClassification && (
                  <p className="mt-1">Classification: <b>{cancelClassification.replace(/_/g, " ")}</b></p>
                )}
                {sh.cancelError && <p className="mt-1">{sh.cancelError}</p>}
                {sh.cancelErrorAt && (
                  <p className="mt-1 opacity-70">Recorded {new Date(sh.cancelErrorAt).toLocaleString("en-IN")}</p>
                )}
              </div>
            )}
            {sh.labelError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2 mt-3"
                data-testid="label-error">
                Packing slip: {sh.labelError} Open Delhivery One for this waybill if it keeps failing.
              </p>
            )}
            {o.paymentMethod === "COD" && o.deliveryMethod === "COURIER" && (
              <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2 mt-3" data-testid="cod-payment-status-note">
                COD remains unpaid until collection is recorded. Delivery status alone does not mark this order paid.
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              {parcelEligible && (
                <Btn variant="accent" onClick={createShipment} loading={busy === "Shipment"}
                  disabled={!parcelConfirmed} data-testid="create-shipment-btn">Create Shipment</Btn>
              )}
              {sh.awb && (!pickupExists || pickupCanRetry) && (
                <Btn variant="outline" onClick={createPickup} loading={busy === "Pickup"} data-testid="create-pickup-btn">
                  {pickupCanRetry ? "Arrange Another Pickup" : "Create Pickup"}
                </Btn>
              )}
              {activeAwb && (
                <>
                  <Btn variant="outline" onClick={syncTracking} loading={busy === "Tracking sync"} data-testid="sync-tracking-btn"><RefreshCw className="h-4 w-4" /> Sync</Btn>
                  <Btn variant="outline" onClick={printLabel} loading={busy === "Print label"}
                    disabled={!labelsEnabled} title={labelsEnabled ? "Print the packing slip" : labelsDisabledHint}
                    data-testid="print-label-btn"><Printer className="h-4 w-4" /> Print label</Btn>
                  <Btn variant="outline" onClick={downloadLabel} loading={busy === "Label"}
                    disabled={!labelsEnabled} title={labelsEnabled ? "Download the packing slip" : labelsDisabledHint}
                    data-testid="download-label-btn"><Download className="h-4 w-4" /> Download</Btn>
                  <a
                    href="https://one.delhivery.com/"
                    target="_blank"
                    rel="noreferrer"
                    className={labelsEnabled
                      ? "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      : "inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-400"}
                    data-testid="label-btn"
                  >
                    <Printer className="h-4 w-4" /> Open Delhivery One
                  </a>
                </>
              )}
              {paid && <Btn variant="outline" onClick={openInvoice} data-testid="invoice-btn"><FileText className="h-4 w-4" /> Invoice</Btn>}
              {o.paymentMethod === "COD" && o.paymentStatus !== "PAID" && o.status === "DELIVERED" && (
                <Btn variant="accent" onClick={recordCODCollection} loading={busy === "COD collection"}
                  data-testid="record-cod-collection-btn">Record COD collection</Btn>
              )}
              {paid && !closed && (
                <Btn variant="outline" onClick={cancelOrder} loading={busy === "Cancel"} data-testid="cancel-order-btn">
                  <XCircle className="h-4 w-4" /> {o.paymentMethod === "COD" ? "Cancel order" : "Cancel & Refund"}
                </Btn>
              )}
              {cancelRequired && (
                <Btn variant="outline" onClick={retryCarrierCancellation}
                  loading={busy === "Retry carrier cancel"} data-testid="retry-carrier-cancel-btn">
                  <RefreshCw className="h-4 w-4" /> Retry carrier cancellation
                </Btn>
              )}
            </div>
            {activeAwb && (
              <p className="text-xs text-slate-500 mt-3" data-testid="label-hint">
                {labelsEnabled
                  ? "Print or download this packing slip here; Delhivery One stays available as a fallback."
                  : `Automated labels are not enabled for this store. In Delhivery One, search for AWB ${sh.awb} and download its Shipping Label.`}
              </p>
            )}
            {sh.trackingUrl && (
              <a href={sh.trackingUrl} target="_blank" rel="noreferrer" className="text-xs text-amber-600 mt-3 inline-block">Track on Delhivery →</a>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Update Status</h3>
            <p className="text-sm text-slate-500 mb-3">Current: <b>{o.status.replace(/_/g, " ")}</b></p>
            {data.allowedTransitions.length === 0 ? <Empty text="No further transitions" /> : (
              <>
                <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} testid="status-select">
                  <option value="">Select a status…</option>
                  {data.allowedTransitions.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </Select>
                <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                <Btn variant="accent" className="w-full mt-3" onClick={updateStatus} loading={saving}
                  disabled={!newStatus} data-testid="update-status-btn">Update Status</Btn>
              </>
            )}
          </Card>
          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-2">Delivery Address</h3>
            <p className="text-sm text-slate-600">{o.address?.fullName}<br />{o.address?.line1}, {o.address?.city}, {o.address?.state} — {o.address?.pincode}<br />{o.address?.phone}</p>
            <p className="text-xs text-slate-500 mt-2">Method: {o.deliveryMethod} · Payment: {o.paymentMethod}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-mono text-slate-700">{value}</span></div>;
